import {
  type AddCreatorFormWithLink,
  AddCreatorRestControllerService,
  ApiError,
  CreatorRestControllerService,
} from '@api'
import {
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { generatePath, matchPath } from 'react-router'

import { LocalStorageKeys } from '@/consts/localStorage.ts'
import { queryClient } from '@/consts/query.ts'
import { Pages } from '@/consts/routes.ts'
import { $currentPage, navigateToFx } from '@/models/router.ts'
import { toast } from '@/models/toasts.ts'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils.ts'
import type { Creator } from '@/modules/creators/types.ts'
import { isActiveCreator, isValidCreator } from '@/modules/creators/utils.ts'
import { SegmentEvents, analytics } from '@/utils/analytics.ts'
import { getCreatorIdFromPath, getInitialCreatorId } from '@/utils/creators.ts'
import { handleError } from '@/utils/error'

export const loadCreatorsFx = createEffect({
  name: 'load creators',
  handler: async () => {
    return CreatorRestControllerService.listCreators() as unknown as Promise<
      Creator[]
    >
  },
})

loadCreatorsFx.failData.watch((error) => {
  handleError(
    error,
    'Something went wrong while loading creators',
    loadCreatorsFx,
  )
})

export const addCreatorFx = createEffect<
  AddCreatorFormWithLink,
  // TODO: probably update types
  void,
  ApiError
>({
  name: 'add creator',
  handler: async (requestBody) => {
    return AddCreatorRestControllerService.addCreatorViaLink({
      requestBody,
    })
  },
})

addCreatorFx.doneData.watch(() => {
  queryClient.invalidateQueries('licenses')
  queryClient.invalidateQueries('creator')

  analytics.track(SegmentEvents.CreatorAdded)
  toast.success('Successfully added creator!')
})

addCreatorFx.failData.watch((error) => {
  queryClient.invalidateQueries('licenses')
  queryClient.invalidateQueries('creator')

  if (error.status === 409) {
    toast.error(
      'You already added this OnlyFans account to your CreatorHero account.',
    )
  } else if (error.status === 423) {
    toast.error(
      'This OnlyFans account has already been added to a CreatorHero trial account. Please end the trial or message our support if you think this is a mistake.',
    )
  } else if (error.status === 404) {
    toast.error(
      "This OnlyFans account was not found. Make sure you didn't restrict US IPs on your OnlyFans account.",
    )
  } else {
    toast.error(
      'Something went wrong. Please contact the support for more information.',
    )
  }
})

export const resetCreators = createEvent<unknown>('reset creators')
export const $creators = createStore<Creator[]>([], {
  name: 'creators list',
})
  .on(loadCreatorsFx.doneData, (_, potentialCreators) => {
    if (potentialCreators.length > 0 && !isValidCreator(potentialCreators[0])) {
      return []
    }

    return potentialCreators.map((creator) => ({
      ...creator,
      name: unescapeHtmlText(creator.name),
    }))
  })
  .reset(resetCreators)

export const $isCreatorProcessCompleted = createStore(false, {
  name: 'is creator process completed',
})
  .on(loadCreatorsFx.doneData, () => true)
  .reset(resetCreators, loadCreatorsFx.failData)

export const setCreatorId = createEvent<number | null>("set creator's id")
export const $creatorId = createStore<number | null>(getInitialCreatorId(), {
  name: "current selected creator's id",
}).on(setCreatorId, (_, id) => id)

// save last selected creator id to localstorage
$creatorId.watch((creatorId) => {
  if (creatorId) {
    localStorage.setItem(
      LocalStorageKeys.PERSISTED_CREATOR_ID,
      creatorId.toString(),
    )
  }
})

export const $currentCreator = combine(
  $creators,
  $creatorId,
  (creators, creatorId) => {
    return creators.find(({ id }) => creatorId === id) || null
  },
)

export const $creatorOFId = $currentCreator.map(
  (data) => data?.onlyfansId ?? null,
)

// select creator based on url or creators list
sample({
  source: {
    page: $currentPage,
    creators: $creators,
  },
  fn: ({ page, creators }) => {
    const idFromPath = getCreatorIdFromPath(page)

    if (idFromPath && creators.some(({ id }) => id === idFromPath)) {
      return idFromPath
    }

    const savedId = parseInt(
      localStorage.getItem(LocalStorageKeys.PERSISTED_CREATOR_ID) ?? '',
    )

    if (savedId && creators.some(({ id }) => id === savedId)) {
      return savedId
    }

    const firstActiveCreatorId = creators.filter((creator) =>
      isActiveCreator(creator),
    )[0]?.id

    if (firstActiveCreatorId) {
      return firstActiveCreatorId
    }

    return null
  },
  target: setCreatorId,
})

// update the current url if creator changed
sample({
  clock: $creatorId,
  source: { page: $currentPage },
  filter: ({ page }, id) => {
    const oldId = getCreatorIdFromPath(page)

    return !!id && !!oldId && id !== oldId
  },
  fn: ({ page }, id) => {
    const isCUI = !!matchPath(Pages.CHATTING_UI, page)

    return isCUI
      ? { path: generatePath(Pages.CHATTING_UI, { creatorId: id!.toString() }) }
      : { path: page.replace(/^\/c\/\d+/, `/c/${id!}`) }
  },
  target: navigateToFx,
})

sample({
  clock: addCreatorFx.doneData,
  target: loadCreatorsFx,
})
